import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, X, Power } from "lucide-react";
import { toast } from "sonner";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, ImageField } from "@/components/admin/fields";
import { AdminShell } from "@/components/admin/AdminShell";
import { OrdersTab } from "@/components/admin/OrdersTab";
import { OrderDetail } from "@/components/admin/OrderDetail";
import { ProductFormPage } from "@/components/admin/ProductFormPage";
import { FlashSaleCard } from "@/components/admin/FlashSaleCard";
import { ReportsTab } from "@/components/admin/ReportsTab";
import { CampaignKitTab } from "@/components/admin/CampaignKitTab";
import { BannerTab } from "@/components/admin/BannerTab";
import { IntegrationsTab } from "@/components/admin/IntegrationsTab";
import { SetsTab } from "@/components/admin/SetsTab";
import { UsersTab } from "@/components/admin/UsersTab";
import { MarketingTab } from "@/components/admin/MarketingTab";
import { DashboardTab } from "@/components/admin/DashboardTab";
import { AiStudioTab } from "@/components/admin/AiStudioTab";

const emptyBrand = { name: "", tagline: "", description: "", category: "", image: "", website: "", instagram_reel: "", instagram_profile: "" };

const FormModal = ({ title, onClose, children, testid }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-md"
    onClick={onClose}
  >
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 32 }}
      transition={{ duration: 0.35 }}
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-2xl bg-white border border-zinc-200 p-8 md:p-10 max-h-[90vh] overflow-y-auto overscroll-contain"
      data-lenis-prevent
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif font-medium text-3xl text-zinc-900">{title}</h2>
        <button onClick={onClose} className="text-zinc-500 hover:text-gold transition-colors duration-300" aria-label="Close">
          <X size={18} />
        </button>
      </div>
      {children}
    </motion.div>
  </motion.div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const ALL_TABS = ["dashboard", "products", "brands", "orders", "reports", "offers", "sets", "banner", "marketing", "studio", "campaigns", "integrations", "users"];
  const visibleTabs = user && user.role === "staff" ? ["dashboard", "orders"] : ALL_TABS;

  useEffect(() => {
    if (user === false) navigate("/admin");
    else if (user && !["admin", "staff"].includes(user.role)) navigate("/");
    else if (user && user.role === "staff") setTab("orders");
  }, [user, navigate]);

  const load = () => {
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => {
      setOrders(r.data);
      setSelectedOrder((sel) => (sel ? r.data.find((o) => o.id === sel.id) || null : null));
    }).catch(() => {});
    api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/admin/sync-status").then((r) => setSyncStatus(r.data)).catch(() => {});
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync");
      const total = data.results.reduce((s, r) => s + (r.synced || 0), 0);
      const failed = data.results.filter((r) => r.error);
      toast.success(`Catalog synced — ${total} products refreshed across ${data.results.length - failed.length} houses`);
      failed.forEach((r) => toast.error(`${r.brand}: sync failed`));
      setSyncStatus({ last_run: data.last_run, results: data.results });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (!user) return null;

  const openProductForm = (p) => {
    setEditProduct(p || null);
    setProductFormOpen(true);
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, { ...p, active: p.active === false ? true : false });
      toast.success(p.active === false ? `${p.name} is live again` : `${p.name} hidden from store`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openBrandForm = (b) => {
    setEditing(b || null);
    setForm(b ? { ...b } : { ...emptyBrand });
  };

  const openCouponForm = (c) => {
    setEditing(c || null);
    setForm(c ? { ...c } : { code: "", kind: "percent", value: "", min_order: "", description: "", active: true });
  };

  const saveCoupon = async (e) => {
    e.preventDefault();
    const payload = { ...form, value: Number(form.value), min_order: Number(form.min_order) || 0 };
    try {
      if (editing) await api.put(`/admin/coupons/${editing.id}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success(editing ? "Coupon updated" : "Coupon created");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveBrand = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/brands/${editing.id}`, form);
      else await api.post("/admin/brands", form);
      toast.success(editing ? "Brand updated" : "Brand added");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (type, id, label) => {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/${type}/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <AdminShell
      tab={tab}
      setTab={(t) => { setTab(t); setSelectedOrder(null); setProductFormOpen(false); }}
      counts={{ orders: orders.length, products: products.length, brands: brands.length, coupons: coupons.length }}
      visibleKeys={visibleTabs}
      user={user}
      onLogout={async () => { await logout(); navigate("/admin"); }}
      onSync={sync}
      syncing={syncing}
      syncStatus={syncStatus}
      headerActions={
        (tab === "products" || tab === "brands" || tab === "offers") && (
          <button
            onClick={() => (tab === "products" ? openProductForm(null) : tab === "brands" ? openBrandForm(null) : openCouponForm(null))}
            data-testid="admin-add-btn"
            className="flex items-center gap-1.5 bg-[#E80071] hover:bg-[#D00065] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
          >
            <Plus size={14} /> Add {tab === "products" ? "Product" : tab === "brands" ? "Brand" : "Coupon"}
          </button>
        )
      }
    >
      <div data-testid="admin-dashboard">
        {tab === "dashboard" ? (
          <DashboardTab />
        ) : tab === "studio" ? (
          <AiStudioTab />
        ) : tab === "campaigns" ? (
          <CampaignKitTab />
        ) : tab === "sets" ? (
          <SetsTab FormModal={FormModal} />
        ) : tab === "users" ? (
          <UsersTab FormModal={FormModal} />
        ) : tab === "marketing" ? (
          <MarketingTab />
        ) : tab === "offers" ? (
          <div data-testid="admin-offers-tab">
            <FlashSaleCard brands={brands} onApplied={load} />
            <div className="mt-6" />
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    <th className="text-left px-5 py-4 font-medium">Code</th>
                    <th className="text-left px-5 py-4 font-medium">Discount</th>
                    <th className="text-left px-5 py-4 font-medium">Min Order</th>
                    <th className="text-left px-5 py-4 font-medium">Status</th>
                    <th className="text-right px-5 py-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-12 text-center text-zinc-400 font-serif italic text-lg">No coupons yet.</td></tr>
                  ) : (
                    coupons.map((c) => (
                      <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200" data-testid={`admin-coupon-row-${c.code}`}>
                        <td className="px-5 py-4 font-mono font-semibold text-zinc-900">{c.code}</td>
                        <td className="px-5 py-4 text-gold">{c.kind === "percent" ? `${c.value}% off` : `${inr(c.value)} off`}</td>
                        <td className="px-5 py-4 text-zinc-500">{c.min_order > 0 ? inr(c.min_order) : "—"}</td>
                        <td className="px-5 py-4">
                          <span className={c.active ? "text-emerald-500" : "text-zinc-400"}>{c.active ? "Live" : "Paused"}</span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openCouponForm(c)} data-testid={`admin-edit-coupon-${c.code}`} className="p-2 text-zinc-400 hover:text-gold transition-colors duration-200" aria-label="Edit">
                              <Pencil size={15} />
                            </button>
                            <button onClick={() => remove("coupons", c.id, c.code)} data-testid={`admin-delete-coupon-${c.code}`} className="p-2 text-zinc-400 hover:text-red-500 transition-colors duration-200" aria-label="Delete">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === "banner" ? (
          <BannerTab />
        ) : tab === "integrations" ? (
          <IntegrationsTab />
        ) : tab === "orders" ? (
          selectedOrder ? (
            <OrderDetail order={selectedOrder} onBack={() => setSelectedOrder(null)} onSaved={load} />
          ) : (
            <OrdersTab orders={orders} onSelect={setSelectedOrder} onSaved={load} />
          )
        ) : tab === "reports" ? (
          <ReportsTab />
        ) : tab === "products" ? (
          productFormOpen ? (
            <ProductFormPage initial={editProduct} brands={brands} onBack={() => setProductFormOpen(false)} onSaved={load} />
          ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto" data-testid="admin-products-table">
            <table className="w-full text-left border-collapse min-w-[820px]">
              <thead>
                <tr>
                  {["Product", "Brand", "Price", "Inventory", "Status", ""].map((h) => (
                    <th key={h} className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4 border-b border-gray-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const disabled = p.active === false;
                  const stockText = p.track_stock ? (p.stock_qty ?? 0) : null;
                  return (
                    <tr key={p.id} className={`hover:bg-gray-50 transition-colors ${disabled ? "opacity-55" : ""}`} data-testid={`admin-product-row-${p.id}`}>
                      <td className="py-3 px-4 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <img src={p.image} alt="" className="w-10 h-12 object-contain bg-gray-50 border border-gray-100 rounded-lg" />
                          <div>
                            <span className="text-gray-900 text-sm">{p.name}</span>
                            {(p.variants || []).length > 0 && <p className="text-xs text-gray-400 mt-0.5">{p.variants.length} variant{p.variants.length > 1 ? "s" : ""}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-[#E80071] text-xs uppercase tracking-wider">{p.brand_slug}</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-gray-700 text-sm">
                        {inr(p.price)}
                        {p.sale_price && p.sale_start && p.sale_end && (
                          <p className="text-[10px] text-emerald-600 mt-0.5" data-testid={`sale-badge-${p.id}`}>Sale ₹{p.sale_price} · {new Date(p.sale_start).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} → {new Date(p.sale_end).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                        )}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-sm">
                        {stockText === null ? (
                          <span className="text-gray-400">Not tracked</span>
                        ) : (
                          <span className={stockText > 0 ? "text-gray-700" : "text-red-500 font-medium"} data-testid={`stock-qty-${p.id}`}>{stockText} in stock</span>
                        )}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${disabled ? "bg-gray-100 text-gray-500 border-gray-200" : "bg-green-100 text-green-800 border-green-200"}`} data-testid={`product-status-${p.id}`}>
                          {disabled ? "Disabled" : "Active"}
                        </span>
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100">
                        <div className="flex justify-end gap-1.5">
                          <button onClick={() => toggleActive(p)} data-testid={`admin-toggle-product-${p.id}`} className={`p-2 rounded-lg transition-colors ${disabled ? "text-gray-400 hover:text-green-600 hover:bg-green-50" : "text-gray-500 hover:text-amber-600 hover:bg-amber-50"}`} aria-label={disabled ? "Enable" : "Disable"} title={disabled ? "Enable" : "Disable"}>
                            <Power size={15} />
                          </button>
                          <button onClick={() => openProductForm(p)} data-testid={`admin-edit-product-${p.id}`} className="p-2 rounded-lg text-gray-500 hover:text-[#E80071] hover:bg-pink-50 transition-colors" aria-label="Edit">
                            <Pencil size={15} />
                          </button>
                          <button onClick={() => remove("products", p.id, p.name)} data-testid={`admin-delete-product-${p.id}`} className="p-2 rounded-lg text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors" aria-label="Delete">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-brands-grid">
            {brands.map((b) => (
              <div key={b.id} className="border border-zinc-200 p-6 flex gap-6 hover:border-gold/30 transition-colors duration-300" data-testid={`admin-brand-card-${b.slug}`}>
                <img src={b.image} alt="" className="w-20 h-24 object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-2xl text-zinc-900">{b.name}</p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold mt-1">{b.category}</p>
                  <p className="text-zinc-400 text-xs mt-2 line-clamp-2">{b.description}</p>
                  <p className="text-zinc-400 text-xs mt-2">{b.product_count ?? 0} products</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => openBrandForm(b)} data-testid={`admin-edit-brand-${b.slug}`} className="p-2 text-zinc-500 hover:text-gold transition-colors duration-200" aria-label="Edit">
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => remove("brands", b.id, b.name)} data-testid={`admin-delete-brand-${b.slug}`} className="p-2 text-zinc-500 hover:text-red-400 transition-colors duration-200" aria-label="Delete">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {form && tab === "offers" && (
          <FormModal title={editing ? "Edit Coupon" : "New Coupon"} onClose={() => setForm(null)} testid="coupon-form-modal">
            <form onSubmit={saveCoupon} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Code" required value={form.code} onChange={set("code")} placeholder="VIRA10" data-testid="coupon-form-code" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Type</label>
                <select value={form.kind} onChange={set("kind")} data-testid="coupon-form-kind" className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60">
                  <option value="percent">Percent off</option>
                  <option value="flat">Flat amount off</option>
                </select>
              </div>
              <Field label={form.kind === "percent" ? "Percent (%)" : "Amount (₹)"} required type="number" step="0.01" value={form.value} onChange={set("value")} data-testid="coupon-form-value" />
              <Field label="Minimum Order (₹)" type="number" step="0.01" value={form.min_order} onChange={set("min_order")} data-testid="coupon-form-min" />
              <div className="sm:col-span-2"><Field label="Description" value={form.description} onChange={set("description")} data-testid="coupon-form-description" /></div>
              <label className="flex items-center gap-3 text-sm text-zinc-600 sm:col-span-2">
                <input type="checkbox" checked={form.active} onChange={set("active")} data-testid="coupon-form-active" className="accent-[#E80071] w-4 h-4" />
                Live (customers can use it)
              </label>
              <button type="submit" data-testid="coupon-form-submit" className="sm:col-span-2 bg-gold text-white text-xs uppercase tracking-[0.3em] py-4 hover:bg-champagne transition-colors duration-300">
                {editing ? "Save Changes" : "Create Coupon"}
              </button>
            </form>
          </FormModal>
        )}
        {form && tab === "brands" && (
          <FormModal title={editing ? "Edit Brand" : "New Brand"} onClose={() => setForm(null)} testid="brand-form-modal">
            <form onSubmit={saveBrand} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name" required value={form.name} onChange={set("name")} data-testid="brand-form-name" />
              <Field label="Category" value={form.category} onChange={set("category")} data-testid="brand-form-category" />
              <div className="sm:col-span-2"><Field label="Tagline" value={form.tagline} onChange={set("tagline")} data-testid="brand-form-tagline" /></div>
              <div className="sm:col-span-2"><ImageField value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="brand-form-image" /></div>
              <div className="sm:col-span-2"><Field label="Website" value={form.website} onChange={set("website")} data-testid="brand-form-website" /></div>
              <div className="sm:col-span-2"><Field label="Instagram Reel URL (shown on this brand's product pages)" value={form.instagram_reel || ""} onChange={set("instagram_reel")} placeholder="https://www.instagram.com/reel/…" data-testid="brand-form-reel" /></div>
              <div className="sm:col-span-2"><Field label="Instagram Profile URL" value={form.instagram_profile || ""} onChange={set("instagram_profile")} placeholder="https://www.instagram.com/brand" data-testid="brand-form-instagram" /></div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Description</label>
                <textarea rows={3} value={form.description} onChange={set("description")} data-testid="brand-form-description" className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300" />
              </div>
              <button type="submit" data-testid="brand-form-submit" className="sm:col-span-2 bg-gold text-white text-xs uppercase tracking-[0.3em] py-4 hover:bg-champagne transition-colors duration-300">
                {editing ? "Save Changes" : "Add Brand"}
              </button>
            </form>
          </FormModal>
        )}
      </AnimatePresence>
    </AdminShell>
  );
}
